import Modal from "Common/Components/Modal";
import React, { useEffect, useState } from "react";
// Formik
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import {
  addPermissions,
  getRoles,
  getResources,
  updatePermissions,
  getPermissions,
  reloadPermissions,
} from "../../../helpers/backend_helper";
import { Common } from "helpers/common";
import { Loader2, UploadCloud } from "lucide-react";

type Props = { onHide: () => void; data: any; onSave: (result: any, closeModal:boolean) => void };

export const AddPermissions: React.FC<Props> = ({ onHide, data, onSave }) => {
  const [permissionData, setPermissionsData] = useState<any>(data);
  const [role, setRole] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any>();
  const [resource, setResource] = useState<any[]>([]);
  const [selectedResource, setSelectedResource] = useState<any>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filteredPermissions, setFilteredPermissions] = useState<any[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[]>([]);
  const commonHelper = new Common();

  const actionTypes = [
    { value: "create", label: "Create" },
    { value: "read", label: "Read" },
    { value: "update", label: "Update" },
    { value: "delete", label: "Delete" },
  ];

  const actionScopes = [
    { value: "any", label: "Any" },
    { value: "own", label: "Own" },
  ];

  // Use boolean for permission toggles
  const [selectedPermissions, setSelectedPermissions] = useState<{[key: string]: boolean}>({});
  const [initialPermissions, setInitialPermissions] = useState<{[key: string]: boolean}>({});

  // Define all permission types as columns
  const permissionTypes = [
    { key: 'read:any', label: 'read:any' },
    { key: 'read:own', label: 'read:own' },
    { key: 'create:any', label: 'create:any' },
    { key: 'create:own', label: 'create:own' },
    { key: 'update:any', label: 'update:any' },
    { key: 'update:own', label: 'update:own' },
    { key: 'delete:any', label: 'delete:any' },
    { key: 'delete:own', label: 'delete:own' },
  ];

  useEffect(() => {
    loadDataFromAPI();
  }, []);

  useEffect(() => {
    if (permissionData?.id) {
      setIsEdit(true);
      setSelectedRole({ value: permissionData.id, label: permissionData.role });
      setSelectedResource({
        value: permissionData.id,
        label: permissionData.resource,
      });
      // Set initial selected permissions if editing
      if (permissionData.action) {
        const [action, scope] = permissionData.action.split(':');
        const initialPerms = { [action]: scope === "any" };
        setSelectedPermissions(initialPerms);
        setInitialPermissions(initialPerms);
      }
    }
  }, [permissionData]);

  const loadDataFromAPI = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadRoles(),
        loadResources(),
        loadAllPermissions()
      ]);
    } catch (error) {
      console.error("Error loading data from API", error);
    } finally {
      setIsLoading(false);
    }
  };

  async function loadRoles() {
    try {
      const response = await getRoles();
      const data = response.data.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      const options = [{ value: "", label: "Select" }, ...data];
      setRole(options);
    } catch (error) {
      console.error("Error loading roles", error);
    }
  }

  async function loadResources() {
    try {
      const response = await getResources();
      const data = response.data.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      const options = [{ value: "", label: "Select" }, ...data];
      setResource(options);
    } catch (error) {
      console.error("Error loading resources", error);
    }
  }

  async function loadAllPermissions() {
    try {
      const response = await getPermissions();
      setAllPermissions(response.data);
    } catch (error) {
      console.error("Error loading permissions", error);
    }
  }

  const loadExistingPermissions = async (role: any) => {
    try {
      setIsLoading(true);
      console.log("All permissions:", allPermissions, role);
      
      const rolePermissions = allPermissions.filter((perm: any) =>
        perm.role === role.label
      );
      
      console.log("Filtered permissions for role:", rolePermissions);

      // Convert permissions to the format we need for the grid
      const formattedPermissions = rolePermissions.reduce((acc: any, perm: any) => {
        if (perm.action) {
          // Create key in format "resource:action:scope"
          const key = `${perm.resource}:${perm.action}`;
          acc[key] = true;
        }
        return acc;
      }, {});

      console.log("Formatted permissions:", formattedPermissions);
      setSelectedPermissions(formattedPermissions);
      setInitialPermissions(formattedPermissions);
      setFilteredPermissions(rolePermissions);
    } catch (error) {
      console.error("Error loading permissions:", error);
      commonHelper.showErrorMessage("Error loading permissions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (item: any) => {
    console.log("Selected role:", item);
    setSelectedRole(item);
    validation.setFieldValue("role", item.value);
    
    if (item?.value) {
      await loadExistingPermissions(item);
    } else {
      // Clear permissions if no role is selected
      setSelectedPermissions({});
      setInitialPermissions({});
      setFilteredPermissions([]);
    }
  };

  const handleResourceChange = (item: any) => {
    setSelectedResource(item);
    validation.setFieldValue("resource", item.value);
  };

  const handlePermissionChange = (actionType: string, scope: string) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [actionType]: scope === "any"
    }));
  };

  const isPermissionSelected = (resourceLabel: string, permKey: string) => {
    const key = `${resourceLabel}:${permKey}`;
    return selectedPermissions[key] === true;
  };

  const handlePermissionToggle = (resourceLabel: string, permKey: string) => {
    const key = `${resourceLabel}:${permKey}`;
    console.log("Toggling permission:", key);
    setSelectedPermissions((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getChangedPermissions = () => {
    const changes: {[key: string]: boolean} = {};
    
    // Find all keys that exist in either object
    const allKeys = new Set([
      ...Object.keys(selectedPermissions),
      ...Object.keys(initialPermissions)
    ]);

    allKeys.forEach(key => {
      if (selectedPermissions[key] !== initialPermissions[key]) {
        changes[key] = selectedPermissions[key];
      }
    });

    return changes;
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      role: selectedRole?.label || "",
    },
    validationSchema: Yup.object({
      role: Yup.string().required("Please Enter A Role!"),
    }),

    onSubmit: async (values: any) => {
      console.log("Form submitted with values:", values);
      console.log("Selected Role:", selectedRole);
      console.log("Selected Permissions:", selectedPermissions);
      
      if (!selectedRole?.value) {
        commonHelper.showErrorMessage("Please select a role");
        setIsSaving(false);
        return;
      }

      setIsSaving(true);
      try {
        const changedPermissions = getChangedPermissions();
        console.log("Changed Permissions:", changedPermissions);
        
        if (Object.keys(changedPermissions).length === 0) {
          commonHelper.showErrorMessage("No changes detected");
          setIsSaving(false);
          return;
        }

        // Create permissions for each changed action
        const permissions = Object.entries(changedPermissions)
          .filter(([_, isSelected]) => isSelected)
          .map(([key]) => {
            // key format is "resourceLabel:action:scope"
            const [resourceLabel, action, scope] = key.split(":");
            return {
              role: selectedRole.label,
              resource: resourceLabel,
              action: `${action}:${scope}`,
              attributes: "*",
              isActive: true
            };
          });

        console.log("Permissions to be saved:", permissions);

        const results = await Promise.all(
          permissions.map(permission => 
            isEdit && permissionData?.id
              ? updatePermissions({ ...permission, id: permissionData.id })
              : addPermissions(permission)
          )
        );

        console.log("API Response:", results);

        commonHelper.showSuccessMessage(
          `Permission${permissions.length > 1 ? 's' : ''} has been successfully ${isEdit ? "updated" : "added"}.`
        );

        onSave(results[0].data, isEdit);
        try {
          await reloadPermissions();
        } catch (error) {
          console.error("Error reloading permissions:", error);
        }
        setIsEdit(false);
      } catch (error: any) {
        console.error("API-Error", error.response?.data);
        commonHelper.showErrorMessage(error.response?.data?.message || "Error saving permissions");
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <React.Fragment>
      <Modal.Header
        className="bg-custom-200 dark:bg-custome-700 flex items-center justify-between p-4 border-b dark:border-zink-300/20"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
      >
        <Modal.Title className="text-16">
          {!!isEdit ? "Edit Permission" : "Add Permission"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_280px)] p-4 overflow-y-auto bg-custom-50 dark:bg-custom-500/200">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="size-8 animate-spin text-custom-500" />
            <span className="ml-2">Loading...</span>
          </div>
        ) : (
          <form
            action="#!"
            onSubmit={(e) => {
              e.preventDefault();
              console.log("Form submit triggered");
              console.log("Form validation errors:", validation.errors);
              console.log("Form touched fields:", validation.touched);
              validation.handleSubmit();
              return false;
            }}
          >
            <div className="mb-4">
              <label htmlFor="role" className="inline-block mb-2 text-base font-medium">
                Role
              </label>
              <Select
                className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500"
                options={role}
                isSearchable={true}
                name="role"
                id="role"
                value={selectedRole}
                onChange={handleRoleChange}
                isDisabled={isSaving}
                onBlur={() => validation.setFieldTouched('role', true)}
              />
              {validation.touched.role && validation.errors.role ? (
                <div id="role-error" className="mt-1 text-sm text-red-500">
                  {validation.errors.role}
                </div>
              ) : null}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full whitespace-nowrap border">
                <thead className="bg-slate-100 dark:bg-zink-600">
                  <tr>
                    <th className="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500">Resource Name</th>
                    {permissionTypes.map((perm) => (
                      <th key={perm.key} className="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500">
                        {perm.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resource.filter(r => r.value).map((res) => (
                    <tr key={res.value}>
                      <td className="px-3.5 py-2.5 border-b border-slate-200 dark:border-zink-500 font-medium">{res.label}</td>
                      {permissionTypes.map((perm) => {
                        const key = `${res.label}:${perm.key}`;
                        const isChecked = isPermissionSelected(res.label, perm.key);
                        return (
                          <td key={perm.key} className="px-3.5 py-2.5 border-b border-slate-200 dark:border-zink-500 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handlePermissionToggle(res.label, perm.key)}
                              disabled={isSaving}
                            />
                            <span className="ml-1">{isChecked ? 'yes' : 'no'}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                data-modal-close="addDocuments"
                className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500"
                onClick={onHide}
                disabled={isSaving}
              >
                Cancel
              </button>
              {!isSaving ? (
                <button
                  type="submit"
                  className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  {!!isEdit ? "Update Permissions" : "Save Permissions"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  className="flex items-center text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                >
                  <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                  Saving...
                </button>
              )}
            </div>
          </form>
        )}
      </Modal.Body>
    </React.Fragment>
  );
};
